import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { UserModel } from '@entities/user'
import {
  createBaseSelector,
  registerSlice,
  StatusType
} from '@shared/lib/redux'
import { AgeFilterType, GenderFilterType } from '@shared/types/filters'
import { AnyDiagramEntry } from '@shared/types/geoTypes'

import { CollectionResponseDTO, CollectionRequestDTO } from '../types'

export interface CollectionStore {
  items: UserModel[]
  page: number
  size: number
  total: number
  phones: number | null
  collectionName: string | null

  ageDiagram: AnyDiagramEntry | null
  genderDiagram: AnyDiagramEntry | null

  ageFilter: AgeFilterType[]
  genderFilter: GenderFilterType
  searchFilter: string

  status: StatusType
  error: string | null
}

const initialState: CollectionStore = {
  items: [],
  page: 1,
  size: 0,
  total: 0,
  phones: 0,
  collectionName: '',

  ageDiagram: null,
  genderDiagram: null,

  ageFilter: ['from18to25', 'from25to35', 'from35to45', 'from45more'],
  genderFilter: 'any',
  searchFilter: '',

  status: 'idle',
  error: null
}

const collectionSlice = createSlice({
  name: 'collectionSlice',
  initialState,
  reducers: {
    getCollection(state, _action: PayloadAction<CollectionRequestDTO>) {
      state.status = 'loading'
    },
    getCollectionSuccess(state, action: PayloadAction<CollectionResponseDTO>) {
      state.items = action.payload.items
      state.page = action.payload.meta.page
      state.size = action.payload.meta.size
      state.total = action.payload.meta.total
      state.phones = action.payload.phones
      state.collectionName = action.payload.name

      state.ageDiagram = action.payload.ageDiagram
      state.genderDiagram = action.payload.genderDiagram

      state.status = 'success'
      state.error = null
    },
    getCollectionFailure(state, action: PayloadAction<string>) {
      state.error = action.payload
      state.status = 'error'
    },
    loadMore(state, _action: PayloadAction<CollectionRequestDTO>) {
      state.status = 'fetching'
    },
    loadMoreSuccess(state, action: PayloadAction<CollectionResponseDTO>) {
      state.items = [...state.items, ...action.payload.items]
      state.page = action.payload.meta.page
      state.total = action.payload.meta.total
      state.status = 'success'
    },
    loadMoreFailed(state, _action: PayloadAction<string>) {
      state.status = 'error'
    },
    create(
      state,
      _action: PayloadAction<{ name: string; items: UserModel[] }>
    ) {
      state.status = 'fetching'
    },
    createSuccess(state) {
      state.status = 'success'
    },
    createFailed(state, action: PayloadAction<string>) {
      state.error = action.payload
      state.status = 'error'
    },
    update(state, _action: PayloadAction<{ id: string; name: string }>) {
      state.status = 'fetching'
    },
    updateSuccess(state, action: PayloadAction<{ name: string }>) {
      state.collectionName = action.payload.name
      state.status = 'success'
    },
    updateFailed(state, action: PayloadAction<string>) {
      state.error = action.payload
      state.status = 'error'
    },
    download(state, _action: PayloadAction<{ id: string }>) {
      state.status = 'fetching'
    },
    downloadSuccess(state) {
      state.status = 'success'
    },
    downloadFailed(state, action: PayloadAction<string>) {
      state.error = action.payload
      state.status = 'error'
    },
    setCollectionName(state, action: PayloadAction<string>) {
      state.collectionName = action.payload
    },
    reset(state) {
      state.items = []
      state.page = 1
      state.size = 0
      state.total = 0
      state.phones = 0
      state.collectionName = ''
      state.ageDiagram = null
      state.genderDiagram = null
      state.status = 'idle'
      state.error = null
    }
  }
})

const collectionBaseSelector = createBaseSelector(collectionSlice)
registerSlice([collectionSlice])

const selectCollectionUsers = createSelector(collectionBaseSelector, (s) => {
  return {
    items: s.items,
    total: s.total,
    phones: s.phones,
    ageData: s.ageDiagram,
    genderData: s.genderDiagram,
    name: s.collectionName,
    status: s.status,
    error: s.error
  }
})

const selectCollectionName = createSelector(
  collectionBaseSelector,
  (s) => s.collectionName
)

export const collectionStore = {
  actions: { ...collectionSlice.actions },
  selectors: {
    selectCollectionUsers,
    selectCollectionName
  },
  name: collectionSlice.name
}
